import { useState, useEffect } from 'react'
import styles from './AddGroupToProjectModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function AddGroupToProjectModal({ 
    isOpen, 
    onClose, 
    onSave, 
    project, 
    apiToken 
}) {
    const [formData, setFormData] = useState({
        group_id: '',
        role: 'member'
    })
    const [availableGroups, setAvailableGroups] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingGroups, setIsLoadingGroups] = useState(false)
    const [error, setError] = useState('')

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !isLoading && !isLoadingGroups)

    useEffect(() => {
        if (isOpen) {
            setFormData({ group_id: '', role: 'member' })
            setError('')
            loadAvailableGroups()
        }
    }, [isOpen])

    const loadAvailableGroups = async () => {
        setIsLoadingGroups(true)
        try {
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.request('/groups')
            
            if (response && response.groups) {
                // Filter out groups who already have access to this project
                const currentGroupIds = project.groups_with_access?.map(g => g.id) || []
                const availableGroups = response.groups.filter(group => 
                    !currentGroupIds.includes(group.id) && group.member_count > 0
                )
                setAvailableGroups(availableGroups)
            } else {
                setError('Failed to load available groups')
            }
        } catch (err) {
            console.error('Error loading groups:', err)
            setError('Failed to load groups')
        } finally {
            setIsLoadingGroups(false)
        }
    }

    if (!isOpen) return null

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsLoading(true)
        setError('')

        try {
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.request(`/groups/${formData.group_id}/projects`, {
                method: 'POST',
                body: JSON.stringify({
                    project_id: project.id,
                    role: formData.role
                })
            })

            if (response.success) {
                const selectedGroup = availableGroups.find(g => g.id === parseInt(formData.group_id))
                onSave(response.group, `Group "${selectedGroup?.name || 'Unknown'}" added to project successfully`)
                onClose()
            } else {
                setError(response.error || 'Failed to add group to project')
            }
        } catch (err) {
            console.error('Failed to add group to project:', err)
            setError(err.message || 'Failed to add group. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const selectedGroup = availableGroups.find(g => g.id === parseInt(formData.group_id))

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2>Add Group to Project</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.projectInfo}>
                        <h3>Project: {project.name}</h3>
                        <p>Adding group access to this project will grant access to all group members</p>
                    </div>

                    {isLoadingGroups ? (
                        <div className={styles.loadingMessage}>
                            Loading available groups...
                        </div>
                    ) : (
                        <>
                            <div className={styles.formGroup}>
                                <label htmlFor="group_id">Select Group *</label>
                                <select
                                    id="group_id"
                                    name="group_id"
                                    value={formData.group_id}
                                    onChange={handleChange}
                                    required
                                    disabled={isLoading}
                                >
                                    <option value="">Choose a group...</option>
                                    {availableGroups.map(group => (
                                        <option key={group.id} value={group.id}>
                                            {group.name} ({group.member_count} member{group.member_count !== 1 ? 's' : ''})
                                        </option>
                                    ))}
                                </select>
                                {availableGroups.length === 0 && (
                                    <div className={styles.helpText}>
                                        All groups already have access to this project or no groups exist.
                                    </div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="role">Project Role *</label>
                                <select
                                    id="role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                    required
                                >
                                    <option value="member">Member - Group members can view and work on tasks</option>
                                    <option value="admin">Admin - Group members can manage project and tasks</option>
                                </select>
                            </div>

                            {selectedGroup && (
                                <div className={styles.groupPreview}>
                                    <div className={styles.groupInfo}>
                                        <div className={styles.groupDetails}>
                                            <strong>{selectedGroup.name}</strong>
                                            <span>{selectedGroup.member_count} member{selectedGroup.member_count !== 1 ? 's' : ''}</span>
                                            {selectedGroup.description && (
                                                <span className={styles.groupDescription}>{selectedGroup.description}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.roleInfo}>
                                        All group members will receive <strong>{formData.role}</strong> access to this project
                                    </div>
                                    <div className={styles.notificationInfo}>
                                        <small>📧 All group members will be notified of this project assignment</small>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div className={styles.modalActions}>
                        <button 
                            type="button" 
                            className={styles.cancelButton}
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className={styles.saveButton}
                            disabled={isLoading || !formData.group_id || isLoadingGroups || availableGroups.length === 0}
                        >
                            {isLoading ? 'Adding...' : 'Add Group'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}